import React, { Component } from "react";
import "./AudioFileLoader.css";

interface Props {
  onUpload: (blob: Blob) => void;
  /** Increment this to force a re-render (i.e. to clear the previous filename) */
  resetTicker: number;
  disabled: boolean;
}
interface State {
  fileName: string;
  isDragDropping: boolean;
}

class AudioFileLoader extends Component<Props, State> {
  fileInputRef = React.createRef<HTMLInputElement>();
  constructor(props: Props) {
    super(props);
    this.state = {
      fileName: "",
      isDragDropping: false,
    };
  }

  dropHandler = (e: DragEvent) => {
    e.preventDefault();
    const dtItems = Array.from(e.dataTransfer?.items || []).filter((i) => i.kind === "file");
    if (dtItems.length) {
      const file = dtItems[0].getAsFile();
      if (file) {
        this.handleNewFile(file);
      }
    }
    this.setState({
      isDragDropping: false,
    });
  };

  dragHandler = (e: DragEvent) => {
    const dtItems = Array.from(e.dataTransfer?.items || []).filter((i) => i.kind === "file");
    e.preventDefault();
    if (e && e.dataTransfer && dtItems.length) {
      if (dtItems.some((item) => item.type.startsWith("audio/"))) {
        e.dataTransfer.dropEffect = "copy";
        if (!this.state.isDragDropping) {
          this.setState({
            isDragDropping: true,
          });
        }
      } else {
        e.dataTransfer.dropEffect = "none";
      }
    }
  };

  dragStopHandler = (e: DragEvent) => {
    e.preventDefault();
    this.setState({
      isDragDropping: false,
    });
  };

  componentDidMount() {
    if ("launchQueue" in window) {
      (window.launchQueue as any).setConsumer(async (launchParams: any) => {
        // https://developer.mozilla.org/en-US/docs/Web/API/LaunchParams/files
        const handles = launchParams.files?.filter((f: any) => f.kind === "file") || [];
        if (handles.length > 0) {
          const fileHandle = handles[0];
          const file = await fileHandle.getFile();
          this.handleNewFile(file);
        }
      });
    }
    // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop
    // Docs for an isolated drop zone - but in this case, there's only one place to drop files
    window.addEventListener("drop", this.dropHandler, true);
    window.addEventListener("dragover", this.dragHandler, true);
    window.addEventListener("dragend", this.dragStopHandler, true);
    window.addEventListener("dragleave", this.dragStopHandler, true);
  }

  componentWillUnmount() {
    window.removeEventListener("drop", this.dropHandler, true);
    window.removeEventListener("dragover", this.dragHandler, true);
    window.removeEventListener("dragend", this.dragStopHandler, true);
    window.removeEventListener("dragleave", this.dragStopHandler, true);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetTicker !== this.props.resetTicker) {
      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = "";
      }
      this.setState({ fileName: "" });
    }
  }

  /* Note: File extends Blob */
  handleNewFile = (file: File) => {
    this.setState({
      fileName: file.name,
    });
    this.props.onUpload(file);
  };

  handleUpload = (event?: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files?.length) {
      this.handleNewFile(event.target.files[0]);
    }
  };

  render() {
    const { disabled } = this.props;
    const { fileName, isDragDropping } = this.state;
    const emptyCss = fileName ? "" : "loader-empty";
    const disableCss = disabled ? "loader-disabled" : "";
    const draggingCss = isDragDropping ? "drag-dropping" : "";
    const labelClasses = `audio-file-loader-label ${emptyCss} ${disableCss} ${draggingCss}`;
    return (
      <span className="audio-file-loader-wrapper">
        <label className={labelClasses} htmlFor="audio-file-input">
          {fileName || "Load an Audio File"}
        </label>
        <input
          ref={this.fileInputRef}
          disabled={disabled}
          onChange={this.handleUpload}
          type="file"
          id="audio-file-input"
          name="audio-file-input"
          accept="audio/*"
        />
      </span>
    );
  }
}

export default AudioFileLoader;
